import type { BoxOpener, DropTableItem } from "@loot-os/api-contract";
import type { CreateLootClient, LootApiLike } from "@loot-os/react";

/** The draft box the preview opener describes, in the SDK's BoxDetail shape. */
export interface PreviewBox {
  id: string;
  name: string;
  slug: string;
  imageUrl: string;
  priceCents: number;
  version: number;
  isCatalog: boolean;
  featured: boolean;
  openedCount: number;
  ev: number;
  dropTable: DropTableItem[];
  /** An unsaved draft has no published version, so the preview shows no commitment. */
  dropTableHash: string;
  refreshedAt: string;
  /** The opener style the preview opens with; absent = the spinner. */
  opener?: Omit<BoxOpener, "theme">;
}

/**
 * Thrown if the opener ever tries to write from the dashboard preview.
 *
 * Unreachable by design — see `previewOpenerClient`. It throws rather than
 * no-ops so a change that flips demo mode off fails loudly here instead of
 * quietly opening boxes and debiting a real shopper's balance.
 */
function unreachable(method: string): never {
  throw new Error(
    `[preview] ${method} must never run in the dashboard preview — the opener is mounted in demo mode. ` +
      "If you are seeing this, demo mode was disabled (a sessionToken or a `client` prop was passed).",
  );
}

/**
 * A client that can only describe the draft box.
 *
 * Handed to `<LootBoxOpener createClient>` rather than its `client` prop, which
 * is the whole trick: `isDemoMode` is `!sessionToken && !client`, so passing the
 * *factory* leaves demo mode on. The SDK then rolls opens and claims locally
 * (`demoOpen`/`demoClaim`) and its unclaimed-reveal resume is skipped outright,
 * so no network write path exists. Only `getBox` is ever called, and it answers
 * from the editor's unsaved draft instead of the API — which is what makes this
 * a preview of what staff are typing rather than of what is published.
 */
export function previewOpenerClient(box: PreviewBox): CreateLootClient {
  return () => {
    const client: LootApiLike = {
      getBox: () => Promise.resolve(box),
      openBox: () => unreachable("openBox"),
      claimReveal: () => unreachable("claimReveal"),
      // Present and null-answering so the SDK never reaches for a real reveal
      // even if the demo-mode guard around it changes.
      getUnclaimedReveal: () => Promise.resolve(null),
    };
    return client;
  };
}
